import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button } from "antd";
import GenericStatCard from "../../../../Components/Cards/GenericStatCard";
import creditCardIcon from "../../../../Assets/Icons/credit_card.svg";
import calendarIcon from "../../../../Assets/Icons/calendar_month.svg";
import clockIcon from "../../../../Assets/Icons/clock.svg";
import locationIcon from "../../../../Assets/Icons/distance.svg";
import approvedImage from "../../../../Assets/Icons/verified.svg";
import refusedImage from "../../../../Assets/Icons/verified_off.svg";
import carIcon from "../../../../Assets/Icons/car.svg";
import truckIcon from "../../../../Assets/Icons/truck.svg";
import fuelIcon from "../../../../Assets/Icons/gray_fuel_icon.svg";
import storeIcon from "../../../../Assets/Icons/storefront.svg";
import driverIcon from "../../../../Assets/Icons/account_circle.svg";
import humidityMidIcon from "../../../../Assets/Icons/humidity_mid.svg";
import humidityVerticalIcon from "../../../../Assets/Icons/humidity_vertical.svg";

import "./style.css";
import SummaryInfoBar from "./SummaryInfoBar";
import TransactionDenialCard from "./TransactionDenialCard";
import GenericInformationCard from "./GenericInformationCard";
import { getTransactionById } from "../../../../API/Admin/Transactions";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const formatLabel = (value) =>
  value
    ? value
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "-";

const formatCardNumber = (cardNumber) =>
  cardNumber ? cardNumber.replace(/(.{4})/g, "$1  ").trim() : "-";

const formatTimestamp = (isoDate) => {
  if (!isoDate) return "-";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const TransactionDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const transactionId = location.state?.id;

  const [txn, setTxn] = useState(null);

  useEffect(() => {
    if (!transactionId) return;

    const fetchTransaction = async () => {
      try {
        const res = await getTransactionById(transactionId);

        if (Math.floor(res?.status / 100) === 2) {
          setTxn(res?.data?.data || null);
        }
      } catch (err) {
        console.error("Failed to load transaction:", err);
      }
    };

    fetchTransaction();
  }, [transactionId]);

  const handleBack = () => {
    navigate(-1);
  };

  const isApproved = txn?.status === "approved";

  return (
    <div className="transaction-detail">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>
      <div className="header-wrapper">
        <div className="title">{txn?.txn_id || "-"}</div>
        <div className="badge">
          <span className={`status-badge ${isApproved ? "active" : "locked"}`}>
            <img
              src={isApproved ? approvedImage : refusedImage}
              alt="Approved/Refused Icon"
            />
            {isApproved ? "Approved" : "Denied"}
          </span>
        </div>
      </div>
      <div className="sub-header-wrapper">
        <div className="icon">
          <img src={creditCardIcon} alt="Fuel Icon" />
        </div>
        <div className="info-label">
          Authorization Method:{" "}
          <span className="sub-text">{formatLabel(txn?.authorization_method)}</span>
        </div>
      </div>
      {isApproved ? (
        <div className="stats-container">
          <GenericStatCard
            title="Total Cost"
            value={formatCurrency(txn?.amount)}
            showBlankLine={false}
          />
          <GenericStatCard
            title="Total Volume"
            value={txn?.volume ? `${txn.volume} L` : "-"}
            showBlankLine={false}
          />
          <GenericStatCard
            title="Fuel Type"
            value={txn?.fuel_type_name || "-"}
            showBlankLine={false}
          />
          <GenericStatCard
            title="Cost Per Liter"
            value={
              txn?.price_per_liter ? `$${txn.price_per_liter}/L` : "-"
            }
            showBlankLine={false}
          />
        </div>
      ) : (
        <TransactionDenialCard
          denialCode={txn?.denial_code ? formatLabel(txn.denial_code) : null}
          requestTimestamp={formatTimestamp(txn?.created_at)}
          reason={txn?.denial_message}
        />
      )}

      <div className="summary-section">
        <SummaryInfoBar
          metrics={[
            {
              label: "Mileage at Transaction",
              value: txn?.mileage_at_transaction ?? "-",
            },
            { label: "Fuel Level", value: txn?.fuel_level ?? "-" },
            {
              label: "Allowed Volume",
              value: txn?.allowed_volume ? `${txn.allowed_volume} L` : "-",
            },
          ]}
          meta={[
            { label: "Date", value: txn?.date || "-", icon: calendarIcon },
            { label: "Time", value: txn?.time || "-", icon: clockIcon },
            {
              label: "Location",
              value:
                txn?.gps_latitude && txn?.gps_longitude
                  ? `${txn.gps_latitude}, ${txn.gps_longitude}`
                  : "-",
              icon: locationIcon,
            },
          ]}
        />
      </div>

      <div className="detailed-card-container">
        <GenericInformationCard
          headerTitle="Vehicle Information"
          headerIcon={carIcon}
          bodyIcon={resolveAvatarUrl(txn?.fleet_company_avatar) || truckIcon}
          isImage={!!resolveAvatarUrl(txn?.fleet_company_avatar)}
          bodyTitleOne={"Vehicle ID"}
          bodySubTitleOne={txn?.vehicle_id || "-"}
          bodyTitleTwo={"License Plate"}
          bodySubTitleTwo={txn?.license_plate || "-"}
          bodyTitleThree={"Fleet Group"}
          bodySubTitleThree={txn?.fleet_company_name || "-"}
          bodyTitleFour={"Odometer"}
          bodySubTitleFour={
            txn?.navixy_odometer !== null && txn?.navixy_odometer !== undefined
              ? `${txn.navixy_odometer} KM`
              : "-"
          }
          validated={isApproved}
        />
        <GenericInformationCard
          headerTitle="Driver Information"
          headerIcon={driverIcon}
          bodyIcon={resolveAvatarUrl(txn?.driver_avatar)}
          bodyInitials={txn?.driver_name}
          isImage={!!resolveAvatarUrl(txn?.driver_avatar)}
          bodyTitleOne={"Name"}
          bodySubTitleOne={txn?.driver_name || "-"}
          bodyTitleTwo={"Driver ID"}
          bodySubTitleTwo={txn?.driver_id || "-"}
          bodyTitleThree={"Card Number"}
          bodySubTitleThree={formatCardNumber(txn?.card_number)}
          validated={isApproved}
        />
        <GenericInformationCard
          headerTitle="Card Information"
          headerIcon={creditCardIcon}
          bodyIcon={resolveAvatarUrl(txn?.driver_avatar)}
          bodyInitials={txn?.driver_name}
          isImage={!!resolveAvatarUrl(txn?.driver_avatar)}
          bodyTitleOne={"Card Holder"}
          bodySubTitleOne={txn?.driver_name || "-"}
          bodyTitleTwo={"Card Number"}
          bodySubTitleTwo={formatCardNumber(txn?.card_number)}
          bodyTitleThree={"Confidence Level"}
          bodySubTitleThree={formatLabel(txn?.confidence_level)}
          bodyTitleFour={"Vehicle Name"}
          bodySubTitleFour={txn?.vehicle_name || "-"}
          validated={isApproved}
        />
        <GenericInformationCard
          headerTitle="Vendor Information"
          headerIcon={storeIcon}
          bodyIcon={resolveAvatarUrl(txn?.fuel_vendor_avatar) || fuelIcon}
          isImage={!!resolveAvatarUrl(txn?.fuel_vendor_avatar)}
          bodyTitleOne={"Vendor Name"}
          bodySubTitleOne={txn?.fuel_vendor_name || "-"}
          bodyTitleTwo={"Pump Number"}
          bodySubTitleTwo={txn?.pump_number || "-"}
          bodyTitleThree={"Address"}
          bodySubTitleThree={txn?.fuel_vendor_address || "-"}
          validated={isApproved}
        />
        <GenericInformationCard
          headerTitle="Fuel Breakdown"
          headerIcon={humidityMidIcon}
          bodyIcon={humidityVerticalIcon}
          bodyTitleOne={"Volume"}
          bodySubTitleOne={txn?.volume ? `${txn.volume} L` : "-"}
          bodyTitleTwo={"Price / Liter"}
          bodySubTitleTwo={
            txn?.price_per_liter ? `$${txn.price_per_liter}` : "-"
          }
          bodyTitleThree={"Total"}
          bodySubTitleThree={formatCurrency(txn?.amount)}
          validated={isApproved}
        />
      </div>
    </div>
  );
};

export default TransactionDetails;
